import { DailyBalance } from '../types/balance';
import { Transaction } from '../types/Transaction';

/**
 * Simple function to calculate dailyBalances array from transaction history
 * 
 * This is a straightforward implementation for app initialization that rebuilds
 * the dailyBalances array from transaction history without complex dependencies.
 * 
 * @param transactions Array of transactions with basic structure
 * @param initialBalance Starting balance
 * @returns Array of DailyBalance objects sorted chronologically
 */
export function calculateDailyBalances(
  transactions: Transaction[], 
  initialBalance: number = 0
): DailyBalance[] {
  try {
    // Group transactions by date
    const transactionsByDate = new Map<string, Transaction[]>();
    
    transactions.forEach((transaction: Transaction) => {
      const date = transaction.date?.toString().split('T')[0] || transaction.date?.toString();
      if (date && !transactionsByDate.has(date)) {
        transactionsByDate.set(date, []);
      }
      if (date) {
        transactionsByDate.get(date)!.push(transaction);
      }
    });

    // Calculate daily balances sequentially
    const dailyBalances: DailyBalance[] = [];
    let runningBalance = initialBalance;
    
    // Sort dates chronologically
    const sortedDates = Array.from(transactionsByDate.keys()).sort();
    
    for (const date of sortedDates) {
      const dayTransactions = transactionsByDate.get(date) || [];
      
      // Calculate daily net change
      let dailyChange = 0;
      const transactionIds: string[] = [];
      
      for (const transaction of dayTransactions) {
        const amount = typeof transaction.amount === 'string' 
          ? Number(transaction.amount) 
          : transaction.amount;
        
        // Apply transaction based on type
        switch (transaction.type) {
          case 'INCOME':
            dailyChange += amount;
            break;
          case 'EXPENSE':
            dailyChange -= amount;
            break;
          case 'SAVING':
            // Savings reduce available balance
            dailyChange -= amount;
            break;
          default:
            // Default to expense behavior for unknown types
            dailyChange -= Math.abs(amount);
        }
        
        transactionIds.push(transaction.id);
      }
      
      // Update running balance
      runningBalance += dailyChange;
      
      dailyBalances.push({
        date,
        balance: runningBalance,
        transactionIds
      });
    }
    
    return dailyBalances;
  } catch (error) {
    console.error('Error calculating daily balances:', error);
    return [];
  }
}

/**
 * Helper function to get the latest balance from dailyBalances array
 */
export function getLatestBalance(dailyBalances: DailyBalance[]): number {
  if (dailyBalances.length === 0) return 0;
  return dailyBalances[dailyBalances.length - 1]?.balance || 0;
}

/**
 * Helper function to get balance for a specific date
 */
export function getBalanceForDate(
  dailyBalances: DailyBalance[], 
  targetDate: string
): number | null {
  const balance = dailyBalances.find(db => db.date === targetDate);
  return balance ? balance.balance : null;
} 